import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { t } from '@lingui/core/macro'
import { Plural } from '@lingui/react/macro'

import { AppLayoutPortal } from '@jotta/ui/AppLayoutPortal'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import SvgX from '@jotta/ui/SvgX'
import { Breakpoint, useBreakpoint } from '@jotta/ui/useBreakpoint'
import { cn } from '@jotta/utils/css'

import type { CollectionRef } from '../../api/useCollection'
import { useAlbumId } from '../../api/useCollection'
import type { SelectablePhoto } from '../../store/SelectionStore'
import { useSelectionStore } from '../../store/SelectionStore'
import { trackPersonPhotosAction } from '../PhotoActions/PhotoAction.tracking'
import type { PhotoActionHandlersProps } from '../PhotoActions/PhotoActionHandlers'
import type { ActionDispatcherProps } from '../PhotoActions/PhotoActions'
import { PhotoAction } from '../PhotoActions/PhotoActions'

import { BaseSelectionManager } from './BaseSelectionManager'

function Dispatcher({
  hasGhostButtons,
  onClick,
  loading,
  icon,
  label,
}: ActionDispatcherProps & { hasGhostButtons: boolean }) {
  const md = useBreakpoint(Breakpoint.md)
  return (
    <Button
      className={cn('btn text-current', {
        'btn-tonal': !hasGhostButtons,
        'btn-ghost': hasGhostButtons,
      })}
      onClick={onClick}
      loading={loading}
      icon={icon}
    >
      {md && label}
    </Button>
  )
}

function OutlineDispatcher(props: ActionDispatcherProps) {
  return <Dispatcher hasGhostButtons={false} {...props} />
}

function GhostDispatcher(props: ActionDispatcherProps) {
  return <Dispatcher hasGhostButtons={true} {...props} />
}

function PhotoSelectionActions({
  collectionRef,
  selectedPhotos,
  hasGhostButtons,
  actionScope,
}: {
  collectionRef: CollectionRef
  selectedPhotos: readonly SelectablePhoto[]
  hasGhostButtons: boolean
  actionScope: 'default' | 'people'
}) {
  if (actionScope === 'people') {
    return (
      <PhotoAction.Provider
        collectionRef={collectionRef}
        photos={selectedPhotos}
        actionScope={actionScope}
        onTrackAction={trackPersonPhotosAction}
        actionDispatcherComponent={
          hasGhostButtons ? GhostDispatcher : OutlineDispatcher
        }
      >
        <PhotoAction.SharePhotos />
        <PhotoAction.AddToAlbum />
        <PhotoAction.DownloadPhotos />
      </PhotoAction.Provider>
    )
  }

  return (
    <DefaultPhotoSelectionActions
      collectionRef={collectionRef}
      selectedPhotos={selectedPhotos}
      hasGhostButtons={hasGhostButtons}
    />
  )
}

function DefaultPhotoSelectionActions({
  collectionRef,
  selectedPhotos,
  hasGhostButtons,
}: {
  collectionRef: CollectionRef
  selectedPhotos: readonly SelectablePhoto[]
  hasGhostButtons: boolean
}) {
  const albumId = useAlbumId(collectionRef)

  return (
    <PhotoAction.Provider
      collectionRef={collectionRef}
      photos={selectedPhotos}
      actionDispatcherComponent={
        hasGhostButtons ? GhostDispatcher : OutlineDispatcher
      }
    >
      <PhotoAction.SharePhotos />
      <PhotoAction.AddToAlbum />
      <PhotoAction.HidePhotos />
      <PhotoAction.UnhidePhotos />
      {albumId ? <PhotoAction.RemoveFromAlbum /> : <PhotoAction.DeletePhotos />}
      <PhotoAction.DownloadPhotos />
    </PhotoAction.Provider>
  )
}

export const SelectionManager = observer<
  Pick<PhotoActionHandlersProps, 'actionContext' | 'addPhotoMode'> & {
    collectionRef: CollectionRef
    selectables: readonly SelectablePhoto[] | undefined
    actionScope?: 'default' | 'people'
  }
>(function SelectionManager({
  collectionRef,
  actionContext,
  selectables,
  actionScope = 'default',
  ...props
}) {
  const { addPhotoMode = '' } = props
  const store = useSelectionStore()
  const { enableShiftSelect, reset } = store
  const selectedPhotos = store.values

  useEffect(() => {
    if (selectables && selectables.length > 0) {
      enableShiftSelect(selectables)
    }
  }, [selectables, enableShiftSelect])

  // reset the store when this component mounts and unmounts
  useEffect(() => {
    reset()
    return reset
  }, [reset])

  const navigate = useNavigate()
  const show = store.hasSelection || Boolean(addPhotoMode)
  const hidden = actionContext === 'TIMELINE_HIDDEN_PHOTOS_SELECTED_PHOTOS'
  const onClose = useCallback(
    (open: boolean) => {
      if (!open) {
        store.clear()
        if (addPhotoMode) {
          navigate(`/photo/album/${addPhotoMode}`)
        }
      }
    },
    [addPhotoMode, navigate, store],
  )

  useEffect(() => {
    if (!show) {
      return
    }
    const keyDownListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose(false)
        e.preventDefault()
      }
    }
    document.addEventListener('keydown', keyDownListener)
    return () => {
      document.removeEventListener('keydown', keyDownListener)
    }
  }, [show, onClose])

  const showModuleNavOverlay = useBreakpoint(Breakpoint.xl)

  if (!show) {
    return null
  }

  if (showModuleNavOverlay) {
    return (
      <AppLayoutPortal slot="moduleNavOverlay">
        <div className="relative h-full w-full rounded-xl">
          <div className="flex h-full items-center justify-between rounded-xl bg-primary">
            <span className="ml-4 font-medium">
              <Plural
                value={store.size}
                one="# photo selected"
                other="# photos selected"
              />
            </span>
            <div className="flex items-center gap-2 pr-4">
              <PhotoSelectionActions
                selectedPhotos={selectedPhotos}
                collectionRef={collectionRef}
                hasGhostButtons={false}
                actionScope={actionScope}
              />
              <button
                className="btn btn-outline px-[13px]"
                aria-label={t`Close`}
                data-testid="SelectionManagerClose"
                onClick={() => onClose(false)}
              >
                <PlainBrandIcon icon={SvgX} height={16} width={16} />
              </button>
            </div>
          </div>
        </div>
      </AppLayoutPortal>
    )
  }

  return (
    <AppLayoutPortal slot="bottomStatusBar">
      <BaseSelectionManager
        hidden={hidden}
        selectionCount={store.size}
        onClose={onClose}
        data-testid="SelectionManager"
        data-actioncontext={actionContext}
      >
        <PhotoSelectionActions
          selectedPhotos={selectedPhotos}
          collectionRef={collectionRef}
          hasGhostButtons={true}
          actionScope={actionScope}
        />
      </BaseSelectionManager>
    </AppLayoutPortal>
  )
})
